import type { Action } from "~/modules/actions/Action";
import type { ContextManager } from "~/modules/contextManager/ContextManager";
import { canHold, getReservationsByTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { getLogger, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";





export interface HoldParticipantOptions {
    


    holdMusicUrl?: string;
    


    holdMusicMethod?: string;
}





















export class HoldParticipant implements Action<Promise<void>> {
    


    #targetParticipantSid: string;

    


    #taskSid: string;

    


    #options?: HoldParticipantOptions;

    constructor(targetParticipantSid: string, taskSid: string, options?: HoldParticipantOptions) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.getInstanceOf(TaskRouterImpl);
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.getInstanceOf(AnalyticsImpl);
        const cbm = context.getInstanceOf(CbmImpl);

        const worker = taskRouter.worker;
        const holdMusicUrl = this.#options?.holdMusicUrl;
        const holdMusicMethod = this.#options?.holdMusicMethod || "GET";

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");
        if (!reservation || !canHold(reservation, worker)) {
            const errorMsg = "holdParticipant: Can't hold participant";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const task = reservation.task;

        const participant = await cbm.getParticipantBySid(this.#targetParticipantSid, task);

        if (participant && participant.type === "external") {
            try {
                await cbm.holdParticipant(task, this.#targetParticipantSid);

                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker);

                return Promise.resolve();
            } catch (error) {
                const errorMsg = `holdParticipant: Could not hold external participant ${this.#targetParticipantSid}`;
                sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, errorMsg);
                throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
            }
        }

        const participants = await cbm.getParticipantsByTask(task);
        const workerParticipant = participants.find(
            (p) => p.routingProperties?.workerSid === this.#targetParticipantSid
        );
        if (workerParticipant && workerParticipant.routingProperties?.workerSid && workerParticipant.type === "agent") {
            await task.hold(workerParticipant.routingProperties?.workerSid, true, {
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                ...(holdMusicMethod && { holdMethod: holdMusicMethod })
            });

            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker);

            return Promise.resolve();
        }

        try {
            await task.updateParticipant({
                hold: true,
                ...(holdMusicUrl && { holdUrl: holdMusicUrl }),
                holdMethod: holdMusicMethod
            });

            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker);

            return Promise.resolve();
        } catch (error) {
            const errorMsg = `holdParticipant: Could not hold participant ${this.#targetParticipantSid}`;
            sendTrackingEvent(EVENTS.HoldParticipantCompleted, analytics, worker, errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }
    }
}
